import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Mail, Paperclip, Send, Clock, CheckCircle, XCircle, Users, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_GROUPS = [
  { value: 'atleta', label: 'Atleti' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'formatore', label: 'Formatori' },
  { value: 'maestro', label: 'Maestri' },
  { value: 'contabile', label: 'Contabili' },
  { value: 'admin', label: 'Admin' },
];

export const AdminMassEmail = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/email/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data);
    } catch (err) {
      console.error('Error fetching email history:', err);
    }
  };

  const toggleGroup = (value) => {
    if (sendToAll) return;
    setSelectedGroups(prev =>
      prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value]
    );
  };

  const handleSendToAllChange = (checked) => {
    setSendToAll(checked);
    if (checked) setSelectedGroups([]);
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Inserisci un oggetto'); return; }
    if (!body.trim()) { toast.error('Inserisci il testo del messaggio'); return; }
    if (!sendToAll && selectedGroups.length === 0) { toast.error('Seleziona almeno un gruppo destinatario'); return; }

    const groups = sendToAll ? 'tutti' : selectedGroups.join(',');
    const groupsLabel = sendToAll ? 'TUTTI gli utenti attivi' : selectedGroups.map(g => ROLE_GROUPS.find(r => r.value === g)?.label).join(', ');

    if (!window.confirm(`Stai per inviare un'email a: ${groupsLabel}\n\nOggetto: ${subject}\n\nConfermi l'invio?`)) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('body', body);
      formData.append('target_groups', groups);
      if (attachment) formData.append('attachment', attachment);

      const res = await axios.post(`${API}/admin/email/send`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });

      const data = res.data;
      if (data.success_count > 0 && data.failure_count === 0) {
        toast.success(`Email inviate con successo a ${data.success_count} destinatari`);
      } else if (data.success_count > 0) {
        toast.warning(`${data.success_count} inviate, ${data.failure_count} fallite`);
      } else {
        toast.error(`Invio fallito per tutti i ${data.failure_count} destinatari`);
      }

      setSubject('');
      setBody('');
      setSelectedGroups([]);
      setSendToAll(false);
      setAttachment(null);
      fetchHistory();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore durante l\'invio';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="admin-mass-email">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-red-600" />
            Invia Email a Utenti
          </CardTitle>
          <CardDescription>
            Invia comunicazioni email da segreteria@cibsdance.it agli utenti attivi
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Target Groups */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Destinatari</Label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="send-to-all"
                checked={sendToAll}
                onChange={(e) => handleSendToAllChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                data-testid="email-send-to-all"
              />
              <Label htmlFor="send-to-all" className="text-sm cursor-pointer font-medium">
                Tutti gli utenti attivi
              </Label>
            </div>
            {!sendToAll && (
              <div className="flex flex-wrap gap-2" data-testid="email-group-selector">
                {ROLE_GROUPS.map(group => (
                  <button
                    key={group.value}
                    onClick={() => toggleGroup(group.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                      selectedGroups.includes(group.value)
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-red-300 hover:text-red-600'
                    }`}
                    data-testid={`email-group-${group.value}`}
                  >
                    {group.label}
                  </button>
                ))}
              </div>
            )}
            {(sendToAll || selectedGroups.length > 0) && (
              <p className="text-xs text-gray-500">
                <Users className="inline h-3 w-3 mr-1" />
                {sendToAll ? 'Tutti gli utenti con profilo attivo' : `Gruppi selezionati: ${selectedGroups.map(g => ROLE_GROUPS.find(r => r.value === g)?.label).join(', ')}`}
              </p>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label htmlFor="email-subject" className="text-sm font-semibold">Oggetto</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Oggetto della comunicazione..."
              data-testid="email-subject-input"
            />
          </div>

          {/* Body */}
          <div className="space-y-1.5">
            <Label htmlFor="email-body" className="text-sm font-semibold">Messaggio</Label>
            <Textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Scrivi il testo della comunicazione..."
              rows={8}
              className="resize-y"
              data-testid="email-body-input"
            />
          </div>

          {/* Attachment */}
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Allegato (opzionale)</Label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <Paperclip className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  {attachment ? attachment.name : 'Scegli file...'}
                </span>
                <input
                  type="file"
                  onChange={(e) => setAttachment(e.target.files[0] || null)}
                  className="hidden"
                  data-testid="email-attachment-input"
                />
              </label>
              {attachment && (
                <button
                  onClick={() => setAttachment(null)}
                  className="text-red-500 hover:text-red-700"
                  data-testid="email-remove-attachment"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !body.trim() || (!sendToAll && selectedGroups.length === 0)}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            data-testid="email-send-button"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Invio in corso...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Invia Email
              </span>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => setShowHistory(!showHistory)}>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              Storico Email Inviate
            </span>
            <Badge variant="outline">{history.length}</Badge>
          </CardTitle>
        </CardHeader>
        {showHistory && (
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Nessuna email inviata</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {history.map((item) => (
                  <div key={item.id} className="border rounded-lg p-3 hover:bg-gray-50" data-testid={`email-history-${item.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.subject}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDate(item.sent_at)} — {item.sent_by_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {item.has_attachment && (
                          <Paperclip className="h-3.5 w-3.5 text-gray-400" />
                        )}
                        {item.success_count > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-green-600">
                            <CheckCircle className="h-3.5 w-3.5" />{item.success_count}
                          </span>
                        )}
                        {item.failure_count > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-red-500">
                            <XCircle className="h-3.5 w-3.5" />{item.failure_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {item.target_groups_display}
                      </Badge>
                      <span className="text-[10px] text-gray-400">{item.total_recipients} destinatari</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default AdminMassEmail;
